import 'components/ordering/quotation/components/request/product-preview.js'
import 'components/ordering/quotation/components/request/quantity.js'
import { CSSResultGroup, html, LitElement, nothing } from 'lit'
import { customElement } from 'lit/decorators.js'
import { map } from 'lit/directives/map.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { classMap } from 'lit/directives/class-map.js'
import { hostDisplayContents, productsStyles } from 'components/ordering/quotation/components/quotation.style.js'
import { QuotationLineDTO } from 'components/ordering/quotation/api/dtos/quotation-line-dto.js'
import { NotFoundError } from 'components/ordering/quotation/api/dtos/error.js'
import { QuotationDTO } from 'components/ordering/quotation/api/dtos/quotation-dto.js'
import { consume } from '@lit/context'
import {
  activeQuotationContext,
  deleteQuotationLineContext, updateQuotationContext
} from 'components/ordering/quotation/components/context/quotation-context.js'
import { isQuotationDTO } from 'components/ordering/quotation/components/is-quotation-dto.js'
import { UpdateQuotationPayload } from 'components/ordering/quotation/api/requests/update-quotation.js'
import { LineIds } from 'components/ordering/quotation/api/payloads/group-accepted-line-ids.js'


initializeIconsByRole({
  'csc-quotation-lines.delete-button': { name: 'trash-can', style: 'light' },
})


type IsGrouped = QuotationLineDTO & { groupCode: string }
const isGrouped = (line: QuotationLineDTO | undefined): line is IsGrouped => line !== undefined && line.groupCode !== null

@customElement('csc-quotation-lines')
export class CSCQuotationLines extends LitElement {
  static override styles: CSSResultGroup = [hostDisplayContents, productsStyles]

  @consume({ context: activeQuotationContext, subscribe: true })
  private quotation!: QuotationDTO | NotFoundError

  private get hideSubtotal(): boolean {
    return isQuotationDTO(this.quotation)
      && this.quotation.priceRequestMode
      && this.quotation.status === 'answered'
  }

  private get lines(): QuotationLineDTO[] {
    if (!isQuotationDTO(this.quotation)) return []
    return this.quotation.quotationLines
  }

  private get groupedLines(): Record<string, QuotationLineDTO[]> {
    if (!isQuotationDTO(this.quotation)) return {}

    return this.lines.reduce((groups, line) => {
      const groupKey = line.groupCode ? line.groupCode : `line-${line.id}`
      if (!groups[groupKey]) {
        groups[groupKey] = []
      }
      groups[groupKey].push(line)
      return groups
    }, {} as Record<string, QuotationLineDTO[]>)
  }

  @consume({ context: deleteQuotationLineContext })
  private deleteQuotationLine!: (quotationId: number, lineId: number) => Promise<void>

  private async deleteLine(lineId: number) {
    if (!isQuotationDTO(this.quotation)) return
    await this.deleteQuotationLine(this.quotation.id, lineId)
  }

  @consume({ context: updateQuotationContext })
  private updateQuotation!: (payload: UpdateQuotationPayload) => Promise<void>

  private selectGroupedLine = async (lineId: number) => {
    if (!isQuotationDTO(this.quotation)) return

    const groupCode: string = this.getGroupCodeForLine(lineId)
    const groupAcceptedLines: Record<string, number> = this.getGroupAcceptedLines()
    const groupAcceptedLineIds: LineIds = Object.values({ ...groupAcceptedLines, [groupCode]: lineId })

    await this.updateQuotation({
      quotationId: this.quotation.id,
      status: this.quotation.status,
      groupAcceptedLineIds: groupAcceptedLineIds,
    })
  }

  private getGroupCodeForLine = (lineId: number): string => {
    const line = this.lines.find(line => line.id === lineId)
    if (isGrouped(line)) return line.groupCode

    throw new Error(`line "${lineId}" to be grouped not found in lines`)
  }

  private getGroupAcceptedLines = (): Record<string, number> => {
    return this.lines.reduce((acc: Record<string, number>, line) => {
      if (isGrouped(line) && line.groupAccepted) {
        acc[line.groupCode] = line.id
      }
      return acc
    }, {})
  }

  renderSelectableButton(selectable: boolean, selected: boolean, lineId: number) {
    if (selectable) {
      return html`
        <cse-radio-button
          .checked="${selected}"
          value="${lineId}"
          @change=${() => this.selectGroupedLine(lineId)}
        ></cse-radio-button>
      `
    }

    return nothing
  }

  renderEditableQuantity(line: QuotationLineDTO) {
    if (this.quotation instanceof NotFoundError) {
      return nothing
    }

    if (!this.quotation.showQuantity) {
      return nothing
    }

    if (this.quotation.status === 'open') {
      return html`
        <csc-quotation-quantity
          class="quantity"
          .quotationId=${this.quotation.id}
          .lineId=${line.id}
        ></csc-quotation-quantity>

        <cse-icon-button
          variant="secondary"
          size="small"
          aria-label="Delete product"
          @click="${() => this.deleteLine(line.id)}"
        >
          <cse-icon icon-role="csc-quotation-lines.delete-button"></cse-icon>
        </cse-icon-button>
      `
    }

    return nothing
  }

  renderReadonlyQuantity(line: QuotationLineDTO) {
    if (this.quotation instanceof NotFoundError) {
      return nothing
    }

    if (!this.quotation.showQuantity) {
      return nothing
    }

    if (this.quotation.status !== 'open') {
      return html`
        <cse-caption-description
          class="quantity"
          alignment="right"
          caption-variant="caption-s"
          description-variant="body"
        >
          <span slot="caption">
            ${t('quantity', { ns: 'csc-quotation-lines', default: 'Quantity' })}
          </span>
          ${line.quantity}
        </cse-caption-description>
      `
    }

    return nothing
  }

  renderLine(line: QuotationLineDTO) {
    if (this.quotation instanceof NotFoundError) {
      return nothing
    }

    const selectable = this.quotation.status === 'answered' && line.groupCode !== null && line.groupCode.trim() !== ''
    const selected = line.groupAccepted === true

    return html`
      <cse-list-item>
        <div
          class="${classMap({
            'grid-container': true,
            'selectable': selectable,
            'quantity-changeable': ['open', 'draft'].includes(this.quotation.status),
            'hide-subtotal': this.hideSubtotal,
          })}"
          @click=${() => selectable ? this.selectGroupedLine(line.id) : null}
        >
          ${this.renderSelectableButton(selectable, selected, line.id)}

          <csc-quotation-product-preview .lineId="${line.id}"></csc-quotation-product-preview>

          ${this.renderEditableQuantity(line)}
          ${this.renderReadonlyQuantity(line)}

          ${this.quotation.showPrice && !this.hideSubtotal ? html`
            <cse-caption-description alignment="right" caption-variant="caption-s" description-variant="body">
              <span slot="caption">
                ${t('subtotal', { ns: 'csc-quotation-lines', default: 'Subtotal' })}
              </span>
              ${line.priceFormatted}
            </cse-caption-description>
          ` : nothing}

          <cse-divider orientation="horizontal" variant="solid"></cse-divider>
        </div>
      </cse-list-item>
    `
  }

  renderGroup(lines: QuotationLineDTO[]) {
    return html`
      <cse-paper variant="info" bordered class="grouped-lines">
        <cse-text as="h4" variant="heading-4">
          ${t('alternative_products', {
            ns: 'csc-quotation-lines',
            default: 'We have different offers, please make your choice'
          })}
        </cse-text>
        ${map(lines, (line) => this.renderLine(line))}
      </cse-paper>
    `
  }

  override render() {
    return html`
      <cse-list>
        ${map(Object.values(this.groupedLines), (lines) => {
          if (lines.length > 1) {
            return this.renderGroup(lines)
          }
          if (lines[0]) {
            return this.renderLine(lines[0])
          }
          return nothing
        })}
      </cse-list>
    `
  }
}
